import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CaretDown, Check } from "@phosphor-icons/react";

export interface InkSelectOption<T extends string = string> {
  value: T;
  label: string;
}

/**
 * The share dialog's select anatomy (#11426 nooks N1, CD ruling): the D1
 * ink menu — a quiet token trigger and a portalled menu of ink rows —
 * replacing the native <select>s the legacy dialog carried. No clear row:
 * every consumer here is a closed choice (a role, a scope) where "no
 * value" is not a state. A long value (the organization's name) wraps in a
 * wide trigger rather than cutting mid-word, and the menu opens upward
 * when opening downward would cover the dialog's footer — the primary is
 * never occluded (CD, after-pass).
 */
function InkSelect<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  disabled,
  compact,
  wrapLabel,
  className,
}: {
  value: T;
  options: InkSelectOption<T>[];
  onChange: (value: T) => void;
  ariaLabel: string;
  disabled?: boolean;
  /** Compact trigger for in-row use (the per-person role menu). */
  compact?: boolean;
  /** A long value wraps in the trigger instead of truncating mid-word
   * (the org-scope select). */
  wrapLabel?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  // The vertical anchor is one or the other, never neither — a compile
  // error, not a silently-unpositioned menu.
  const [pos, setPos] = useState<
    | ({ top: number } & { left: number; width: number })
    | ({ bottom: number } & { left: number; width: number })
    | null
  >(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  const toggle = () => {
    if (disabled) return;
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      // Flip upward when the space below can't hold the menu — the
      // dialog's footer is never occluded. Estimated from the option
      // count (rows are ~40px, wrapped org names at most double one row).
      const estimatedMenuHeight = options.length * 40 + 24;
      const spaceBelow = window.innerHeight - rect.bottom;
      const flipUp = spaceBelow < estimatedMenuHeight;
      setPos({
        ...(flipUp
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
        left: rect.left,
        width: Math.max(rect.width, 148),
      });
    }
    setOpen((v) => !v);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // The menu stands down before the dialog does.
        e.stopPropagation();
        setOpen(false);
      }
    };
    // The menu is fixed at open-time coordinates; scrolling any ancestor
    // (the dialog's own people list included) would detach it from its
    // trigger, so it closes instead.
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={toggle}
        disabled={disabled}
        className={`inline-flex items-center gap-1.5 min-w-0 rounded-md border border-[var(--fp-border-strong)] bg-[var(--fp-surface)] text-[var(--fp-ink)] transition-colors ${
          disabled
            ? "opacity-60 cursor-not-allowed"
            : "cursor-pointer hover:border-[var(--fp-ink-3)]"
        } ${compact ? "px-2 py-1 text-xs" : "px-3 py-2 text-sm"} ${
          className ?? ""
        }`}
      >
        <span
          className={
            wrapLabel ? "min-w-0 text-left leading-snug" : "truncate min-w-0"
          }
        >
          {selected?.label ?? value}
        </span>
        <CaretDown
          size={compact ? 11 : 13}
          className={`flex-shrink-0 text-[var(--fp-ink-3)] transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            role="listbox"
            aria-label={ariaLabel}
            className="fixed bg-[var(--fp-surface)] border border-[var(--fp-border)] rounded-lg [box-shadow:var(--fp-lift-overlay)] py-1 overflow-hidden"
            style={{
              ...("top" in pos ? { top: pos.top } : { bottom: pos.bottom }),
              left: pos.left,
              minWidth: pos.width,
              maxWidth: 320,
              zIndex: 10050,
            }}
          >
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={option.value === value}
                onClick={() => {
                  setOpen(false);
                  if (option.value !== value) onChange(option.value);
                }}
                className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm text-[var(--fp-ink)] hover:bg-[var(--fp-band-hover)]"
              >
                <span>{option.label}</span>
                {option.value === value && (
                  <Check
                    size={13}
                    weight="bold"
                    className="flex-shrink-0 text-[var(--fp-ink-2)]"
                  />
                )}
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
}

export default InkSelect;
